import { AccessGroupsTable, SectionHead } from '../../components';
import { GET_ACCESS_GROUPS } from '@/common/actions/get';
import { DELETE_ACCESS_GROUP } from '@/common/actions/delete';
import {
  accessGroupsInstructionTypeForFilter,
  activeTab,
  getVariables,
  queryParamsInitial,
} from '../../store';
import { useTableQueryParams } from '../../store/useTableQueryParams';
import { ModalCreateAccessGroup } from '../../modals/ModalCreateAccessGroup';
import { ModalDeleteAccessGroup } from '../../modals/ModalDeleteAccessGroup';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Box } from '@servicepipe/ui-components';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const initialSelectedGroup = { id: null, name: '' };

export const AccessGroupsView = ({ isShowTable }) => {
  const { id: partnerClientAccountId } = useParams();
  const {
    searchParams,
    setSearchParams,
    queryParams,
    onPageChange,
    onSortingChange,
    onFiltersChange,
  } = useTableQueryParams({ ...queryParamsInitial, tab: 'accessGroups' });

  const [isCreateModalShow, setIsCreateModalShow] = useState(false);
  const [isDeleteModalShow, setIsDeleteModalShow] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(initialSelectedGroup);

  const [getAccessGroups, { data, loading, refetch }] = useLazyQuery(GET_ACCESS_GROUPS);
  const [deleteAccessGroup, { loading: deleteLoading }] = useMutation(DELETE_ACCESS_GROUP, {
    onCompleted: () => {
      toast.success('Success');
      refetch();
      setIsDeleteModalShow(false);
      setSelectedGroup(initialSelectedGroup);
    },
    onError: (error) => toast.error(error.message),
  });

  useEffect(() => {
    if (queryParams.tab !== activeTab.accessGroups || !partnerClientAccountId) {
      return;
    }

    const variables = getVariables(queryParams, accessGroupsInstructionTypeForFilter, {
      pageSizeKey: 'limit',
      includeDeletedAtNull: !queryParams.isShowDeletedItems,
    });

    getAccessGroups({
      variables: {
        ...variables,
        filterPartnerClientAccountId: Number(partnerClientAccountId),
        deletedAt: queryParams.isShowDeletedItems ? undefined : null,
      },
      fetchPolicy: 'network-only',
    });
  }, [queryParams, partnerClientAccountId]);

  const handleToggleDeleted = (event) => {
    const isChecked = Boolean(event?.target?.checked);
    searchParams.set('page', String(queryParamsInitial.page));
    searchParams.set('isShowDeletedItems', String(Number(isChecked)));
    setSearchParams(searchParams, { replace: true });
  };

  if (!isShowTable) {
    return null;
  }

  return (
    <Box gap="40px" direction="column">
      <SectionHead
        title="Access groups"
        checkboxLabel="Show deleted groups"
        checkboxChecked={queryParams.isShowDeletedItems}
        onCheckboxChange={handleToggleDeleted}
        buttonLabel="+ Create group"
        onButtonClick={() => setIsCreateModalShow(true)}
      />

      <ModalCreateAccessGroup
        isShow={isCreateModalShow}
        onClose={() => setIsCreateModalShow(false)}
        onCreated={() => refetch()}
      />

      {isDeleteModalShow && (
        <ModalDeleteAccessGroup
          groupName={selectedGroup.name}
          loading={deleteLoading}
          onClose={() => {
            setIsDeleteModalShow(false);
            setSelectedGroup(initialSelectedGroup);
          }}
          onConfirm={() =>
            selectedGroup.id && deleteAccessGroup({ variables: { id: selectedGroup.id } })
          }
        />
      )}

      <AccessGroupsTable
        data={data?.accessGroups?.items || []}
        onPageChange={onPageChange}
        onSortingChange={onSortingChange}
        onFiltersChange={onFiltersChange}
        queryParams={queryParams}
        totalPages={data?.accessGroups?.info?.totalPages || 1}
        totalCount={data?.accessGroups?.info?.totalCount || 0}
        loading={loading}
        onDeleteGroup={(group) => {
          setSelectedGroup(group);
          setIsDeleteModalShow(true);
        }}
      />
    </Box>
  );
};

export default AccessGroupsView;
